import appSettingsMinLastPriceUpdateMutations from "@/components/settings-menu/minLastPriceUpdate.mutations.js";
import appSettingsSellersCategoriesMutations from "@/components/settings-menu/sellersCategories.mutations.js";
import appSettingsSellersIdsMutations from "@/components/settings-menu/sellersIds.mutations.js";
import Vue from "vue";
import {
  ClassificationDoesNotExists,
  ClassificationsEmpty,
} from "../exceptions";
import storage from "../local-storage";
import { makeTree } from "../tree";

/* Load the mutations for the app settings */
const {
  loadAppSettingsMinLastUpdateFromStorage,
  setAppSettingsMinLastPriceUpdate,
} = appSettingsMinLastPriceUpdateMutations(storage);
const {
  loadAppSettingsSellersCategoriesFromStorage,
  setAppSettingsSellersCategories,
} = appSettingsSellersCategoriesMutations(storage);
const {
  loadAppSettingsSellersIdsFromStorage,
  setAppSettingsSellersIds,
} = appSettingsSellersIdsMutations(storage);

/**
 * Set the classifications in this module.
 * @param {Object} state The state of this module.
 * @param {Object[]} classifications The array with classifications to set.
 * Each classification is an object like `{id, name, parentId}`.
 */
function classifications(state, classifications) {
  state.classifications = makeTree(classifications);
}

/**
 * Clear all the filters of the filters sidebar.
 * @param {Object} state
 */
function clearFilters(state) {
  setOnlyItemsWithTheirBestPrediction(state, false);
  setLikePatternBrandFilter(state, null);
  setLikePatternCategoryFilter(state, null);
  setLikePatternNameFilter(state, null);
  setNotLikePatternFilter(state, null);
  setShowItemsWithoutPredictions(state, true);
  setShowOnlyCorrectPredictions(state, false);
}

/**
 * A function to call to clear the items because any filter changed. Useful to set the message
 * to indicate that the user must finish setting the filters and search again.
 * @param {Object} state
 */
function clearItemsBecauseFilterChanged(state) {
  setItems(state, []);
  setGetItemsResultMessage(
    state,
    "Los filtros cambiaron, debes aplicarlos nuevamente"
  );
}

/**
 * A mutation to load the app settings from the local storage.
 *
 * @param {Object} state
 */
function loadAppSettingsFromStorage(state) {
  loadAppSettingsMinLastUpdateFromStorage(state);
  loadAppSettingsSellersIdsFromStorage(state);
  loadAppSettingsSellersCategoriesFromStorage(state);
}

/**
 * Set the loading flag for the classifications.
 * @param {Object} state The state of this module.
 * @param {Boolean} loading The state of the loading flag for the classifications.
 */
function loadingClassifications(state, loading) {
  state.loadingClassifications = loading;
}

/**
 * Set the loading flag of the samples.
 * @param {Object} state  The state of this module.
 * @param {Boolean} loading The state of the loading flag for the samples.
 */
function loadingSamples(state, loading) {
  state.loading.samples = loading;
}

function loadingClassRanking(state, loading) {
  state.loading.classRanking = loading;
}

/**
 * Set the sample products of the current actual classification.
 * @param {Object} state The state of this module.
 * @param {Object[]} samples The samples to set in the state.
 */
function samples(state, samples) {
  state.samples = samples;
}

function classRanking(state, classRanking) {
  state.classRanking = classRanking;
}


/**
 * Set the correct property on all the items.
 * @param {Object} state
 * @param {Boolean} correct the correctness value (can be null)
 */
function setAllItemsCorrectness(state, correct) {
  state.items = state.items.map((item) => ({ ...item, correct }));
}

/**
 * AVOID USING THIS MUTATION DIRECTLY. Please use the action `getCategoriesBySellers` instead.
 * Set the categories obtained by sellers ids.
 * @param {Object} state
 * @param {OBject[]} categories
 */
function setCategoriesBySellers(state, categories) {
  state.categoriesBySellers = categories;
}

/**
 * AVOID USING THIS MUTATION DIRECTLY. Please use the `setClassificationsSellerId` action instead.
 *
 * @param {Object} state
 * @param {Number} id
 */
function setClassificationsSellerId(state, id) {
  const numberId = Number(id);
  if (id && Number.isNaN(numberId))
    throw new Error("Please provide a number as id");
  state.classificationsSellerId = id ? numberId : null;
}

/**
 * AVOID USING THIS MUTATION DIRECTLY. Please use the `setCurrentClassification` action instead
 * to make it reactive and get the categories again.
 * Set the current classification.
 * @param {Object} state The state of this module.
 * @param {String} id The id of the classification to set as current.
 */
function setCurrentClassification(state, id) {
  if (id && !state.classifications.length) {
    throw new ClassificationsEmpty();
  }

  const classification = state.classifications.find(
    (classification) => classification.id == id
  );
  if (id && !classification)
    throw new ClassificationDoesNotExists(`Wrong id: ${id}.`);

  state.currentClassification = classification;
  samples(state, []); // Reset the samples of the classification
}

/**
 * Avoid using this mutation, is used by the `getItems` action to set the message of the result
 * if there are no items.
 * @param {Object} state
 * @param {String} message
 */
function setGetItemsResultMessage(state, message) {
  state.getItemsResultMessage = message;
}

/**
 * Set the flag that indicates if the `getItems` action must get the validated items or not.
 * If this flag is true the app loads the already validated items instead of the ones
 * with predictions to validate.
 * @param {Object} state
 * @param {Boolean} value
 */
function setGetValidatedItems(state, value) {
  state.getValidatedItems = value;
  setItems(state, []);
}

/**
 * Set the items with predictions of the current classification.
 * @param {Object} state
 * @param {Object[]} items
 */
function setItems(state, items) {
  state.items = items;
}

/**
 * Set the amount of items to get every time we call the action `getItems`.
 * @param {Object} state
 * @param {Number} fetch
 */
function setItemsFetch(state, fetch) {
  state.itemsFetch = fetch;
}

/**
 * Avoid using this mutation, is for the `getItems` action.
 * Set the number of total items that we can get for the current classification.
 * @param {Object} state
 * @param {Number} total
 */
function setItemsTotal(state, total) {
  state.itemsTotal = total;
}

/**
 * Set the like pattern for the brand to use as filter for the items.
 * @param {Object} state
 * @param {String} likePattern
 */
function setLikePatternBrandFilter(state, likePattern) {
  state.likePatternBrandFilter = likePattern;
  // clearItemsBecauseFilterChanged(state)
}

function setTermSample(state, term) {
  state.termSample = term;
  // clearItemsBecauseFilterChanged(state)
}

/**
 * Set the like pattern for the category to use as filter for the items.
 * @param {Object} state
 * @param {String} likePattern
 */
function setLikePatternCategoryFilter(state, likePattern) {
  state.likePatternCategoryFilter = likePattern;
  // clearItemsBecauseFilterChanged(state)
}

/**
 * Set the like pattern for the name of the items to use it as filter.
 * @param {Object} state
 * @param {String} likePattern
 */
function setLikePatternNameFilter(state, likePattern) {
  state.likePatternNameFilter = likePattern;
  // clearItemsBecauseFilterChanged(state)
}

/**
 * AVOID USING THIS MUTATION DIRECTLY. It is for the `getCategoriesBySellers` action.
 * Set the flag that indicates that the app is loading the categories by sellers.
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingCategoriesBySellers(state, loading) {
  state.loadingCategoriesBySellers = loading;
}

/**
 * AVOID USING THIS MUTATION DIRECTLY. It is for the `createClassification` action.
 * Set the flag that indicates that the app is creating a classification.
 *
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingCreateClassification(state, loading) {
  state.loadingCreateClassification = loading;
}

/**
 * Set the flag that indicates if the app is getting the items with predictions for the
 * current classification.
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingItems(state, loading) {
  state.loadingItems = loading;
}

/**
 * Avoid using this mutation directly.
 * Set the flag that indicates that the `getItemsTotal` action is loading.
 *
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingItemsTotal(state, loading) {
  state.loadingItemsTotal = loading;
}

/**
 * AVOID USING THIS MUTATION DIRECTLY. Is for the `mergeClassifications` action.
 * Set the flag that indicates that the app is merging some classifications.
 *
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingMergeClassifications(state, loading) {
  state.loadingMergeClassifications = loading;
}

/**
 * Set the flag that indicates that the app is saving the correction to the predictions.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingSaveCorrection(state, value) {
  state.loadingSaveCorrection = value;
}

/**
 * Avoid using this mutation, is used by the `getSellersWithPredictions` action.
 * Set the flag that indicates that the app is getting the sellers with predictions.
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingSellersWithPredictions(state, loading) {
  state.loadingSellersWithPredictions = loading;
}

/**
 * Set the not like pattern to filter the items.
 * @param {Object} state
 * @param {String} notLikePattern
 */
function setNotLikePatternFilter(state, notLikePattern) {
  state.notLikePatternFilter = notLikePattern;
  // clearItemsBecauseFilterChanged(state)
}

/**
 * Set the flag that indicates that the items to get will have their best prediction for the current
 * classification.
 * @param {Object} state
 * @param {Boolean} flag
 */
function setOnlyItemsWithTheirBestPrediction(state, flag) {
  state.onlyItemsWithTheirBestPrediction = flag;
  clearItemsBecauseFilterChanged(state);
}

/**
 * Set the classifications that are selected for a merge.
 *
 * @param {Object} state
 * @param {Object[]} classifications
 */
function setSelectedClassificationsForMerge(state, classifications) {
  if (!Array.isArray(classifications)) {
    throw new Error("Please provide an array of classifications");
  }

  state.selectedClassificationsForMerge = classifications;
}

/**
 * Set the flag that indicates if the sidebar that contains the filters over the items with
 * predictions
 * @param {Object} state
 * @param {Boolean} show
 */
function setShowFiltersSidebar(state, show) {
  state.showFiltersSidebar = show;
}


/**
 * Set the flag that indicates that the app can get the items without predictions too.
 * Very useful when using the like and not like patterns to get items that the model did not
 * make a correct prediction.
 * @param {Object} state
 * @param {Boolean} value
 */
function setShowItemsWithoutPredictions(state, value) {
  state.showItemsWithoutPredictions = value;
}

/**
 * Set the value for the flag that indicates that te app must get only items validated as correct
 * for the current classification in the validated items view.
 * @param {Object} state
 * @param {Boolean} value
 */
function setShowOnlyCorrectPredictions(state, value) {
  state.showOnlyCorrectPredictions = value;
}

/**
 * Show or hide the samples sidebar.
 * @param {Object} state The state of this module.
 * @param {Boolean} show The value to set for the flag of the SamplesSidebar.
 */
function setShowSamplesSidebar(state, show) {
  state.showSamplesSidebar = show;
}

function setShowClassRankingSidebar(state, show) {
  state.showClassRankingSidebar = show;
}

/**
 * Set all the items that have `correct !== true` and `correct !== false` as correct or incorrect
 * according to the given correctness flag.
 * @param {Object} state
 * @param {Boolean} correctness of the unmarked items ()`true = correct`, `false = incorrect`)
 */
function setUnmarkedItemsCorrectness(state, correctness) {
  state.items = state.items.map((item) => ({
    ...item,
    correct:
      item.correct !== true && item.correct !== false
        ? correctness
        : item.correct,
  }));
}

/**
 * Toggle the correct state of the given item.
 * @param {Object} state The state of this module.
 * @param {Object} item
 */
function toggleCorrect(state, item) {
  const index = state.items.findIndex(
    ({ id, sellerId }) => item.id === id && item.sellerId === sellerId
  );
  item = state.items[index];

  if (item.correct) item.correct = false;
  else if (item.correct === null) item.correct = true;
  else item.correct = null;

  Vue.set(state.items, index, item);
}

export default {
  classifications,
  clearFilters,
  loadAppSettingsFromStorage,
  loadingClassifications,
  loadingSamples,
  loadingClassRanking,
  samples,
  classRanking,
  setAllItemsCorrectness,
  setAppSettingsMinLastPriceUpdate,
  setAppSettingsSellersCategories,
  setAppSettingsSellersIds,
  setCategoriesBySellers,
  setClassificationsSellerId,
  setCurrentClassification,
  setGetItemsResultMessage,
  setGetValidatedItems,
  setItems,
  setItemsFetch,
  setItemsTotal,
  setLikePatternBrandFilter,
  setTermSample,
  setLikePatternCategoryFilter,
  setLikePatternNameFilter,
  setLoadingCategoriesBySellers,
  setLoadingCreateClassification,
  setLoadingItems,
  setLoadingItemsTotal,
  setLoadingMergeClassifications,
  setLoadingSaveCorrection,
  setLoadingSellersWithPredictions,
  setNotLikePatternFilter,
  setOnlyItemsWithTheirBestPrediction,
  setSelectedClassificationsForMerge,
  setShowFiltersSidebar,
  setShowItemsWithoutPredictions,
  setShowOnlyCorrectPredictions,
  setShowSamplesSidebar,
  setShowClassRankingSidebar,
  setUnmarkedItemsCorrectness,
  toggleCorrect,
};
